import type { BaseQueryFn, QueryActionCreatorResult, QueryDefinition } from '@reduxjs/toolkit/query';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';
import Notification from '@rio-cloud/rio-uikit/Notification';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import { SystemAdminDialogTypes } from 'helper/types';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useCreateSystemFleetMutation } from 'services/systemAdmin/fleets';
import { useLazyListSystemTeamsQuery } from 'services/systemAdmin/teams';
import { clearActiveElements, setSystemAdminOpenDialog } from 'store/slices/settingsPageSlice';
import {
    addSelectedTeamsToFleetForm,
    clearFleetFormData,
    getNameFromFleetForm,
    getSelectedTeamsFromFleetForm,
    getSelectedVehiclesFromFleetForm,
    setFormFleetName,
} from 'store/slices/systemFleet';

import EditFleetInfoTooltip from './EditFleetInfoTooltip';
import InputText from './InputText';
import SelectVehiclesTable from './SelectVehiclesTable';
import type { SystemFleetFormData } from './types';

import type { SystemFleet } from 'API';

import './_styles.scss';

interface Props {
    systemTeams?: SelectOption[];
    isLoadingSystemTeams?: boolean;
    isFetchingFleets?: boolean;
    refetch(): QueryActionCreatorResult<
        QueryDefinition<string, BaseQueryFn<any, unknown, unknown, {}, {}>, never, SystemFleet[], 'systemFleets'>
    >;
}

const SystemFleetCreateForm = ({ systemTeams, isLoadingSystemTeams, isFetchingFleets, refetch }: Props) => {
    const inputTeamsId = 'create-fleet-input-teams';
    const dispatch = useDispatch();
    const nameFromFleetForm = useSelector(getNameFromFleetForm);
    const selectedTeamsFromFleetForm = useSelector(getSelectedTeamsFromFleetForm);
    const selectedVehiclesFromFleetForm = useSelector(getSelectedVehiclesFromFleetForm);

    const [createFleet, { isLoading: isSubmitting }] = useCreateSystemFleetMutation({
        fixedCacheKey: 'updateSystemFleet',
    });

    const [fetchSystemTeams] = useLazyListSystemTeamsQuery();

    const closeDialogs = () => {
        dispatch(setSystemAdminOpenDialog(undefined));
    };

    const openCancelEditingDialog = () => {
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.EXIT_CONFIRMATION));
    };

    const closeFlow = () => {
        dispatch(clearActiveElements());
        fetchSystemTeams('ListSystemTeams');
        closeDialogs();
        dispatch(clearFleetFormData());
        Notification.success(<div />, {
            title: `${nameFromFleetForm} fleet was created`,
            timeout: 10_000,
            closeButton: true,
            hideProgressBar: false,
        });
    };

    const refreshTable = async () => {
        await refetch()
            .unwrap()
            .then(() => closeFlow());
    };

    const onSubmit = async (formData: SystemFleetFormData) => {
        try {
            await createFleet({
                ...formData,
                teams: selectedTeamsFromFleetForm ?? [],
                vehicles: selectedVehiclesFromFleetForm ?? [],
            }).unwrap();
        } catch (error) {
            refetch();
            console.error(error);
            dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.ERROR_DIALOG));
        } finally {
            await refreshTable();
            dispatch(clearFleetFormData());
        }
    };

    const defaultValues = useMemo(
        () => ({
            name: undefined,
        }),
        []
    );

    const { register, formState, getValues, handleSubmit, setValue, setError, clearErrors } =
        useForm<SystemFleetFormData>({
            defaultValues,
        });

    const [isFormValid, setIsValidForm] = useState(true);

    const checkValues = () => {
        if (!getValues(['name'])[0].length) {
            setIsValidForm(false);
            setError('name', {});
        } else {
            clearErrors();
            setIsValidForm(true);
        }
    };

    const setFleetName = (name: string) => {
        setValue('name', name);
        dispatch(setFormFleetName(name));
    };

    const selectTeams = (teams: string[]) => {
        dispatch(addSelectedTeamsToFleetForm(teams));
    };

    return (
        <form className='SystemFleetForm' onChange={checkValues} onSubmit={handleSubmit(onSubmit)}>
            <div className='SystemFleetForm__section'>
                <p className='SystemFleetForm__headline'>Operational</p>
                <div className='SystemFleetForm__line SystemFleetForm__line--largeSpacing SystemFleetForm__line--grid'>
                    <InputText
                        label='Name'
                        name='name'
                        defaultValue={nameFromFleetForm}
                        error={formState.errors.name}
                        errorMessage='Mandatory field'
                        register={register}
                        onChange={e => setFleetName(e.currentTarget.value)}
                        required
                    />
                    <div className='SystemFleetForm__formInput'>
                        <label className='SystemFleetForm__inputLabel' htmlFor={inputTeamsId}>
                            Team(s)
                        </label>
                        <Multiselect
                            id={inputTeamsId}
                            className='SystemFleetForm__multiselect'
                            placeholder='Please select'
                            noItemMessage='No teams available.'
                            options={systemTeams}
                            disabled={!systemTeams}
                            value={selectedTeamsFromFleetForm}
                            onChange={e => selectTeams(e)}
                            isLoading={isLoadingSystemTeams}
                            useFilter
                        />
                    </div>
                </div>
            </div>
            <div className='SystemFleetForm__section'>
                <p className='SystemFleetForm__headline'>Vehicles</p>
                <p className='SystemFleetForm__message'>
                    These vehicles are not assigned to any fleet yet
                    <EditFleetInfoTooltip />
                </p>
                <div className='SystemFleetForm__line'>
                    <SelectVehiclesTable />
                </div>
            </div>
            <div className='SystemFleetForm__footer'>
                <FormDialogFooter
                    onConfirmLabel={
                        isSubmitting || isFetchingFleets ? (
                            <p>Creating fleet</p>
                        ) : (
                            <p>
                                <span className='rioglyph rioglyph-plus SystemFleetForm__submitIcon' />
                                Create new fleet
                            </p>
                        )
                    }
                    onCancel={openCancelEditingDialog}
                    isValid={isFormValid && !!nameFromFleetForm}
                    isLoading={isSubmitting || isFetchingFleets}
                />
            </div>
        </form>
    );
};

export default SystemFleetCreateForm;
